export function getFilename(path: string | undefined) {
  if (!path) return ""
  const trimmed = path.replace(/[/\\]+$/, "")
  const parts = trimmed.split(/[/\\]/)
  return parts[parts.length - 1] ?? ""
}

export function getDirectory(path: string | undefined) {
  if (!path) return ""
  const trimmed = path.replace(/[/\\]+$/, "")
  const parts = trimmed.split(/[/\\]/)
  return parts.slice(0, parts.length - 1).join("/") + "/"
}

export function getFileExtension(path: string | undefined) {
  if (!path) return ""
  const parts = path.split(".")
  return parts[parts.length - 1]
}

export function getFilenameTruncated(path: string | undefined, maxLength: number = 20) {
  const filename = getFilename(path)
  if (filename.length <= maxLength) return filename
  const lastDot = filename.lastIndexOf(".")
  const ext = lastDot <= 0 ? "" : filename.slice(lastDot)
  const available = maxLength - ext.length - 1 // -1 for ellipsis
  if (available <= 0) return filename.slice(0, maxLength - 1) + "…"
  return filename.slice(0, available) + "…" + ext
}

// encodeURI passes `#` and `?` through (they're reserved), but in a file://
// URL they'd start a fragment/query and fileURLToPath would silently truncate
// the path — and both are valid filename characters ("Report #3.pptx").
function encodeFileUrlPath(p: string): string {
  return encodeURI(p).replace(/#/g, "%23").replace(/\?/g, "%3F")
}

// An absolute OS path in any shape fileUrlFromPath can convert: POSIX (/a/b),
// Windows drive-letter (C:\a or C:/a), or UNC (\\server\share). Relative paths
// return false — building a file:// URL from one would be meaningless.
export function isAbsolutePath(p: string): boolean {
  return p.startsWith("/") || /^[A-Za-z]:[\\/]/.test(p) || /^\\\\/.test(p)
}

// file:// URL for an absolute OS path, in the three shapes the server's
// fileURLToPath (and Electron's shell) can resolve:
//   POSIX  /a/b          → file:///a/b
//   Windows C:\a\b       → file:///C:/a/b  (a bare "file://C:" would parse the
//                          drive letter as the URL authority and throw; a bare
//                          encodeURI would percent-encode the backslashes,
//                          leaving zero `/` after file:// — an unusable URL)
//   UNC    \\srv\share\a → file://srv/share/a (host = server)
export function fileUrlFromPath(p: string): string {
  if (/^[A-Za-z]:[\\/]/.test(p)) return "file:///" + encodeFileUrlPath(p.replace(/\\/g, "/"))
  if (/^\\\\/.test(p)) return "file://" + encodeFileUrlPath(p.slice(2).replace(/\\/g, "/"))
  return "file://" + encodeFileUrlPath(p)
}

export function truncateMiddle(text: string, maxLength: number = 20) {
  if (text.length <= maxLength) return text
  const available = maxLength - 1 // -1 for ellipsis
  const start = Math.ceil(available / 2)
  const end = Math.floor(available / 2)
  return text.slice(0, start) + "…" + text.slice(-end)
}
